import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useLang } from "@/i18n";

const links = [
  { key: "chiSono", href: "/#manifesto" },
  { key: "cronostoria", href: "/#cronostoria" },
  { key: "yurax", href: "/yurax" },
  { key: "attestati", href: "/titoli" },
  { key: "stampa", href: "/#stampa" },
  { key: "galleria", href: "/galleria" },
  { key: "contatti", href: "/#contatti" },
];

const LangSwitcher = ({ dark }) => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-full border border-[#DCEEF5] bg-white/80 p-1" data-testid="lang-switcher">
      {["it", "en", "es"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide transition-colors duration-300 ${lang === l ? "bg-[#0284C7] text-white" : "text-[#5F7E8A] hover:text-[#0284C7]"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const renderLink = (l) => {
    const label = t.nav[l.key];
    const testid = `nav-${l.key.toLowerCase()}`;
    if (l.href.startsWith("/#")) {
      return location.pathname === "/" ? (
        <a key={l.key} href={l.href.slice(1)} data-testid={testid}
          className="whitespace-nowrap text-sm font-medium text-[#3D5A66] hover:text-[#0284C7] transition-colors duration-300"
          onClick={() => setOpen(false)}>
          {label}
        </a>
      ) : (
        <Link key={l.key} to={l.href} data-testid={testid}
          className="whitespace-nowrap text-sm font-medium text-[#3D5A66] hover:text-[#0284C7] transition-colors duration-300"
          onClick={() => setOpen(false)}>
          {label}
        </Link>
      );
    }
    return (
      <Link key={l.key} to={l.href} data-testid={testid}
        className="whitespace-nowrap text-sm font-medium text-[#3D5A66] hover:text-[#0284C7] transition-colors duration-300"
        onClick={() => setOpen(false)}>
        {label}
      </Link>
    );
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass-nav border-b border-[#DCEEF5] py-3" : "bg-transparent py-5"}`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex shrink-0 items-center font-display tracking-tight text-lg sm:text-xl font-extrabold text-[#0B2530]">
          <img src="/images/logo.png" alt="Logo Yuri Selvaggio" className="h-9 w-auto" />
        </Link>
        <nav className="hidden xl:flex items-center gap-7">
          {links.map(renderLink)}
          <LangSwitcher />
          <a href={location.pathname === "/" ? "#contatti" : "/#contatti"} data-testid="nav-cta-parla-con-yuri"
            className="group inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#0B2530] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#0284C7] transition-colors duration-300 active:scale-95">
            {t.nav.cta}
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </nav>
        <div className="xl:hidden flex items-center gap-3">
          <LangSwitcher />
          <button className="text-[#0B2530]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden glass-nav border-t border-[#DCEEF5] mt-3 px-6 py-5 flex flex-col gap-4">
          {links.map(renderLink)}
          <Link to="/#contatti" data-testid="nav-mobile-cta"
            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0B2530] text-white text-sm font-semibold px-5 py-3"
            onClick={() => setOpen(false)}>
            {t.nav.cta} <ArrowUpRight size={15} />
          </Link>
        </div>
      )}
    </motion.header>
  );
};
